import * as THREE from "three";
import type { ExtendedMesh } from "../three-extensions";

const FREE_THREE_OBJECT_BUDGET_MS = 4;

/**
 * Free the GPU resources associated with a single ephemeral three.js object.
 * @param obj Object to process
 */
export function freeThreeObject3D(obj: THREE.Object3D): void {
  if (isGeometryObject3D(obj)) {
    if (obj instanceof THREE.Mesh && typeof (obj as ExtendedMesh).dispose === "function") {
      if (!obj.userData.isCached) {
        (obj as ExtendedMesh).dispose();
      }
    }
    if (!obj.geometry.userData?.isCached) {
      obj.geometry.dispose();
    }
  }
  // `shadow` lives on the shadow-casting Light subclasses, not on the Light base class. These three
  // are exactly the lights that own one; Ambient/Hemisphere/RectArea/LightProbe have no shadow.
  if (obj instanceof THREE.DirectionalLight || obj instanceof THREE.SpotLight || obj instanceof THREE.PointLight) {
    obj.shadow.dispose();
  }
  if (obj.userData?.customDispose && typeof obj.userData.customDispose === "function") {
    // Free ephemeral resources such as textures in GPU memory associated with this object.
    obj.userData.customDispose();
  }
}

/**
 * Free an object tree incrementally when the browser is idle.
 * Detach the root from its scene before calling this function.
 */
export function freeThreeObject3DWhenIdle(root: THREE.Object3D): void {
  const objects: THREE.Object3D[] = [];
  root.traverse((object) => objects.push(object));
  let nextObjectIndex = 0;

  const scheduleBatch = (): void => {
    if (typeof globalThis.requestIdleCallback === "function") {
      globalThis.requestIdleCallback(freeBatch, { timeout: 1000 });
      return;
    }
    setTimeout(() => freeBatch(), 0);
  };

  const freeBatch = (deadline?: IdleDeadline): void => {
    if (!deadline?.didTimeout && deadline && deadline.timeRemaining() <= 1) {
      scheduleBatch();
      return;
    }

    const batchStartTime = performance.now();
    while (nextObjectIndex < objects.length) {
      freeThreeObject3D(objects[nextObjectIndex]);
      nextObjectIndex += 1;
      if (
        performance.now() - batchStartTime >= FREE_THREE_OBJECT_BUDGET_MS ||
        (!deadline?.didTimeout && deadline && deadline.timeRemaining() <= 1)
      ) {
        break;
      }
    }
    if (nextObjectIndex < objects.length) {
      scheduleBatch();
    }
  };

  scheduleBatch();
}

/**
 * A more robust check than checking the type of the object.
 */
export function isGeometryObject3D(obj: THREE.Object3D): obj is THREE.Object3D & { geometry: THREE.BufferGeometry } {
  return "geometry" in obj && obj.geometry instanceof THREE.BufferGeometry;
}

/**
 * Clear a three.js scene and free GPU memory associated with ephemeral scene geometry (without isCached userData flag).
 * @param scene Scene to clear.
 * @param excludeChild Direct child of the scene that's left in place, for example a reference grid.
 */
export function clearThreeScene(scene: THREE.Scene, excludeChild?: THREE.Object3D): void {
  for (const child of [...scene.children]) {
    if (child === excludeChild) {
      continue;
    }

    child.traverse(freeThreeObject3D);

    // Generally persistent objects like scene lighting should be added under the "staticContent" object.
    // Clear everything else with the exception of cameras.
    if (!(child instanceof THREE.Camera)) {
      scene.remove(child);
    }
  }
}
